import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Skeleton, Spin } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/redux";
import { Ticket as TicketType } from "../../../../types";
import { fetchTicketsByStatus } from "../../../../redux/features/board/columnsAsyncThunk";
import { ColumnWrapper, IconWrapper, LoadingWrapper, SkeletonCard, TicketList } from "./styles";
import Ticket from "./Ticket";
import { Droppable } from "react-beautiful-dnd";
import ColumnHeader from "./Header";
import { Filters, mergeFilters, setDeleteReason, setReportType } from "../../../../redux/features/board/columnsSlice";
import { ArrowUpOutlined } from "@ant-design/icons";
import { ReportType } from "../../../../redux/features/board/columnTypes";
import { FoundStatuses } from "../../../../types/FoundItemTypes";
import { columnOrderConfig } from "../const";
import { LostStatuses } from "../../../../types/LostStatuses";
import { TicketWrapper } from "./Ticket/styles";

// Removed: dynamic Review header tint based on majority card state.
//
// In the new flow, "green = actionable" lives exclusively on the InProcess column
// (permanently green-themed in statusThemes.ts) — past-window Review cards auto-move
// there via the hourly cron. Letting Review's header still go green by majority would
// re-introduce the ambiguity the rework was meant to fix. Header always renders the
// default Review amber from statusThemes.ts.

interface ColumnProps {
    status: number;
    title: string;
    icon: any;
    type?: ReportType;
}

const Column: React.FC<ColumnProps> = ({ status, title, icon, type }) => {
    const dispatch = useAppDispatch();
    const ticketListRef = useRef<HTMLDivElement>(null);
    const columnWrapperRef = useRef<HTMLDivElement>(null);
    const showLoadMore = useRef<any>(null);
    const openedColumnType = useAppSelector((state) => state.columns.openedColumnType);
    const tickets: TicketType[] = useAppSelector((state) => state.columns.tickets[status] || []);
    const loading = useAppSelector((state) => state.columns.loading[status]);
    const offset = useAppSelector((state) => state.columns.offsets[status] || 0);
    const count = useAppSelector((state) => state.columns.counts[status] || 0);
    const hasMore = useAppSelector((state) => state.columns.hasMore[status]);
    const openedColumn = useAppSelector((state) => state.columns.openedColumn);
    const filters = useAppSelector((state) => state.columns.filters);
    const reason = useAppSelector((state) => state.columns.deleteReason);
    const shipFilter = useAppSelector((state) => state.columns.shipFilter);
    const [isScrolled, setIsScrolled] = useState(false);
    // Inside Column component
    const fetchTimeout = useRef<NodeJS.Timeout | null>(null);
    // Review's header no longer flips color by majority — it always renders default amber.
    // InProcess holds the green/actionable cards instead.
    const headerColorOverride: string | null = null;

    const getTickets = async () => {
        if (openedColumn === FoundStatuses.Discarded && openedColumnType === ReportType.Found) {
            const columnType = ReportType.Found;
            const order = columnOrderConfig[`${status}-${columnType}`];
            await dispatch(fetchTicketsByStatus({ status, offset: 0, limit: 50, filters: { ...filters, status: FoundStatuses.Discarded }, type: columnType, order: order, reason, shipFilter }));
            return;
        }
        const columnType = type ?? ReportType.Lost;
        const order = columnOrderConfig[`${status}-${columnType}`];
        await dispatch(fetchTicketsByStatus({ status, offset: 0, limit: 50, filters, type: columnType, order: order, reason, shipFilter }));
    };
    useEffect(() => {
        if (tickets.length === 0 && !loading) {
            if (fetchTimeout.current) clearTimeout(fetchTimeout.current);

            fetchTimeout.current = setTimeout(() => {
                getTickets();
            }, 100); // Adjust debounce delay as needed
        }
        return () => {
            if (fetchTimeout.current) clearTimeout(fetchTimeout.current);
        };
    }, [status, filters, type, openedColumn, openedColumnType, reason, shipFilter]);

    const handleSameTicketStatus = async () => {
        dispatch(mergeFilters({ searchValue: "" }));
        dispatch(setDeleteReason(""));
    };

    useEffect(() => {
        if (!loading && (openedColumn === FoundStatuses.Discarded || openedColumn === LostStatuses.Deleted)) {
            handleSameTicketStatus();
        }
    }, [openedColumn, openedColumnType]);

    const loadMoreTickets = async () => {
        if (!loading && hasMore) {
            const columnType = type ?? ReportType.Lost;
            const order = columnOrderConfig[`${status}-${columnType}`];
            await dispatch(fetchTicketsByStatus({ status, offset, limit: 10, filters, type: columnType, order: order, reason, shipFilter }));
        }
    };

    const handleScroll = async () => {
        if (!ticketListRef.current || loading || !hasMore) return;
        const { scrollTop, scrollHeight, clientHeight } = ticketListRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
            await loadMoreTickets();
        }
    };

    useEffect(() => {
        const checkHeight = () => {
            if (ticketListRef.current && columnWrapperRef.current) {
                const ticketListHeight = ticketListRef.current.scrollHeight;
                const columnWrapperHeight = columnWrapperRef.current.clientHeight;
                showLoadMore.current = ticketListHeight < columnWrapperHeight;
            }
        };
        checkHeight();
        const observer = new MutationObserver(checkHeight);
        if (ticketListRef.current) {
            observer.observe(ticketListRef.current, { childList: true, subtree: true });
        }
        return () => observer.disconnect();
    }, [tickets, loading]);

    useEffect(() => {
        const handleScrollShowBtn = () => {
            if (ticketListRef.current) {
                const scrollTop = ticketListRef.current.scrollTop;
                setIsScrolled(scrollTop > 50);
            }
        };

        if (ticketListRef.current) {
            ticketListRef.current.addEventListener("scroll", handleScrollShowBtn);
        }

        return () => {
            if (ticketListRef.current) {
                ticketListRef.current.removeEventListener("scroll", handleScrollShowBtn);
            }
        };
    }, []);

    useEffect(() => {
        if (openedColumn === FoundStatuses.Discarded && openedColumnType === ReportType.Found) {
            dispatch(setReportType(ReportType.Found));
        } else {
            dispatch(setReportType(ReportType.Lost));
        }
    }, [openedColumn, openedColumnType]);

    return (
        <ColumnWrapper ref={columnWrapperRef}>
            <ColumnHeader title={title} icon={icon} count={count} status={status} closable={!!openedColumn} colorOverride={headerColorOverride} />
            {loading && tickets.length === 0 ? (
                <>
                    {openedColumn ? (
                        <>
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", width: "100%" }}>
                            <TicketList isGrid={!!openedColumn} onScroll={handleScroll} ref={ticketListRef}>
                                    {Array.from({ length: 20 }).map((_, index) => (
                                        <SkeletonCard key={`skeleton-${index}`}>
                                            <Skeleton active paragraph={{ rows: 5 }} />
                                        </SkeletonCard>
                                    ))}
                                </TicketList>
                            </div>
                        </>
                    ) : (
                        <TicketList isGrid={!!openedColumn} onScroll={handleScroll} ref={ticketListRef}>
                            {Array.from({ length: 6 }).map((_, index) => (
                                <SkeletonCard key={`skeleton-small-${index}`}>
                                    <Skeleton active paragraph={{ rows: 4 }} />
                                </SkeletonCard>
                            ))}
                        </TicketList>
                    )}
                </>
            ) : openedColumn ? (
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", width: "100%" }}>
                    <TicketList isGrid={!!openedColumn} onScroll={handleScroll} ref={ticketListRef}>
                        {tickets.length > 0 ? tickets.map((ticket, index) => <Ticket key={ticket.id || index} ticket={ticket} index={index} />) : <div style={{ textAlign: "center" }}>No data found</div>}
                    </TicketList>
                    {loading && (
                        <LoadingWrapper>
                            <Spin />
                        </LoadingWrapper>
                    )}
                    {showLoadMore && hasMore && !loading && (
                        <Button type="link" onClick={loadMoreTickets}>
                            Load more
                        </Button>
                    )}
                    {isScrolled && (
                        <IconWrapper onClick={() => ticketListRef.current?.scrollTo({ top: 0, behavior: "smooth" })}>
                            <ArrowUpOutlined />
                        </IconWrapper>
                    )}
                </div>
            ) : (
                <Droppable isDropDisabled={true} droppableId={`${status}`}>
                    {(provided) => (
                        <TicketList
                            count={count}
                            fullHeight={!hasMore}
                            isGrid={!!openedColumn}
                            ref={(ref) => {
                                ticketListRef.current = ref;
                                provided.innerRef(ref);
                            }}
                            {...provided.droppableProps}
                            onScroll={handleScroll}
                        >
                            {tickets.length > 0 ? tickets.map((ticket, index) => <Ticket key={ticket.id || index} columnStatus={status} ticket={ticket} index={index} />) : <div style={{ textAlign: "center" }}>No data found</div>}
                            {loading && (
                                <LoadingWrapper>
                                    <Spin />
                                </LoadingWrapper>
                            )}
                            {showLoadMore && hasMore && !loading && (
                                <Button type="link" onClick={loadMoreTickets}>
                                    Load more
                                </Button>
                            )}
                        </TicketList>
                    )}
                </Droppable>
            )}
        </ColumnWrapper>
    );
};

export default Column;
